/**
 * @module data/capabilities
 *
 * The twelve disciplines that run *through* the ADM rather than sitting inside
 * a single phase. Phases are the calendar; capabilities are the enduring assets
 * the organisation keeps afterwards.
 *
 * The `phases` array on each capability is derived from the phase definitions in
 * {@link module:data/phases} at module load, so the two views can never fall out
 * of sync.
 */

import { PHASES } from "./phases.js";

/**
 * @typedef {Object} Capability
 * @property {string}   id          Stable slug.
 * @property {string}   name        Display name.
 * @property {string}   short       Compact label for dense layouts.
 * @property {string}   tagline     One-line description.
 * @property {string}   body        What the capability covers.
 * @property {string}   outcome     The observable result when it is strong.
 * @property {string}   glyph       Key into the SVG icon factory.
 * @property {string}   group       Grouping: `design`, `deliver` or `sustain`.
 * @property {string[]} phases      Phase ids where the capability is most active.
 * @property {number}   maturity    Typical starting maturity (1–5) across clients.
 */

/**
 * @typedef {Object} CapabilitySeed
 * @property {string} id
 * @property {string} name
 * @property {string} short
 * @property {string} tagline
 * @property {string} body
 * @property {string} outcome
 * @property {string} glyph
 * @property {string} group
 * @property {number} maturity
 */

/** @type {CapabilitySeed[]} */
const SEEDS = [
  {
    id: "business-architecture",
    name: "Business Architecture",
    short: "Business Arch.",
    tagline: "Capabilities, value streams and the shape of the enterprise.",
    body: "We model what the business does — as capabilities and value streams rather than org charts — and use that model to decide what must change, what must stay, and what should never have existed.",
    outcome: "A capability map the whole executive team signs, with an owner per capability.",
    glyph: "layers",
    group: "design",
    maturity: 3,
  },
  {
    id: "process-intelligence",
    name: "Process Intelligence",
    short: "Process",
    tagline: "Evidence-based process redesign, not workshop opinion.",
    body: "Process mining, effort analysis and outcome measurement tell us where friction actually lives. We then redesign the work around what the customer needs, not around what the system makes easy.",
    outcome: "A measured reduction in cycle time and rework on the redesigned streams.",
    glyph: "pulse",
    group: "design",
    maturity: 2,
  },
  {
    id: "data-ai-architecture",
    name: "Data & AI Architecture",
    short: "Data & AI",
    tagline: "Contracts, lineage, models and retrieval designed as one system.",
    body: "Data architecture and AI architecture are one discipline. We define entity ownership, quality thresholds and lineage, then design the retrieval, model and evaluation layers on top of them.",
    outcome: "AI services whose inputs, provenance and behaviour can be explained on demand.",
    glyph: "database",
    group: "design",
    maturity: 2,
  },
  {
    id: "application-integration",
    name: "Application & Integration",
    short: "Apps & Integr.",
    tagline: "A portfolio that earns its keep, wired coherently.",
    body: "We rationalise the application estate and specify the interfaces, events and integration patterns that let services — human or agent — compose safely.",
    outcome: "A smaller, documented portfolio with contractual interfaces and no invisible coupling.",
    glyph: "plug",
    group: "deliver",
    maturity: 3,
  },
  {
    id: "operating-model",
    name: "Operating Model & Organisation",
    short: "Operating Model",
    tagline: "Structure follows capability, not history.",
    body: "We design the target operating model around the capabilities the enterprise needs: who decides, who delivers, what is centralised and what is federated, and where product teams sit.",
    outcome: "An operating model with clear decision rights and no orphaned accountabilities.",
    glyph: "people",
    group: "deliver",
    maturity: 3,
  },
  {
    id: "value-portfolio",
    name: "Value & Portfolio",
    short: "Value",
    tagline: "Fund outcomes, sequence change, prove the benefit.",
    body: "Every work package carries a value hypothesis, a cost, a risk and an owner. We sequence the portfolio against absorption capacity and hold it to its benefit case.",
    outcome: "A funded roadmap where each increment has a measurable value milestone.",
    glyph: "target",
    group: "deliver",
    maturity: 3,
  },
  {
    id: "change-adoption",
    name: "Change & Adoption",
    short: "Change",
    tagline: "Adoption is the deliverable. Everything else is inventory.",
    body: "We plan the human side of change with the same rigour as the technical side: impact assessment, champion networks, communication, training and leading adoption indicators.",
    outcome: "Usage and confidence curves that rise from week one instead of stalling at launch.",
    glyph: "rocket",
    group: "deliver",
    maturity: 3,
  },
  {
    id: "talent-enablement",
    name: "Talent & Enablement",
    short: "Talent",
    tagline: "Leave behind people who can run the method without us.",
    body: "Capability transfer is part of the engagement, not an upsell. We build role-based curricula, pair with your architects, and hand over the artefacts, standards and habits.",
    outcome: "An internal architecture and AI practice that outlives the programme.",
    glyph: "book",
    group: "sustain",
    maturity: 2,
  },
  {
    id: "governance-risk",
    name: "Architecture Governance",
    short: "Governance",
    tagline: "Decisions made once, in the open, and enforced.",
    body: "Boards, decision rights, dispensations, compliance reviews and change control — the machinery that keeps a multi-year transformation honest between gates.",
    outcome: "A governance cadence where exceptions are rare, visible and time-bound.",
    glyph: "gantt",
    group: "sustain",
    maturity: 3,
  },
  {
    id: "security-risk",
    name: "Security, Resilience & Risk",
    short: "Security",
    tagline: "Designed-in protection for systems that now act on their own.",
    body: "Threat modelling, entitlement design, resilience engineering and AI-specific risk controls: prompt injection, data exfiltration, tool abuse and uncontrolled agency.",
    outcome: "A risk register where every material exposure has an owner and a control.",
    glyph: "server",
    group: "sustain",
    maturity: 3,
  },
  {
    id: "responsible-ai",
    name: "Responsible AI",
    short: "Responsible AI",
    tagline: "Fairness, transparency and accountability as engineering practice.",
    body: "We turn responsible-AI principles into testable controls: evaluation datasets, refusal behaviour, bias monitoring, human-review sampling and decision explainability for the people affected.",
    outcome: "Automated decisions that survive regulatory scrutiny and public trust.",
    glyph: "compass",
    group: "sustain",
    maturity: 1,
  },
  {
    id: "measurement-analytics",
    name: "Measurement & Analytics",
    short: "Measurement",
    tagline: "If it is not instrumented, it is not managed.",
    body: "We define the metric tree up front — outcome, adoption, quality and cost — and wire the instrumentation so benefit tracking starts with delivery rather than after it.",
    outcome: "A live value dashboard that reconciles to the original business case.",
    glyph: "sliders",
    group: "sustain",
    maturity: 2,
  },
];

/**
 * Build the reverse index from phase → capability into capability → phases.
 *
 * @param {string} capabilityId
 * @returns {string[]}
 */
function phasesFor(capabilityId) {
  return PHASES.filter((phase) => phase.capabilities.includes(capabilityId)).map((phase) => phase.id);
}

/** @type {Capability[]} */
export const CAPABILITIES = SEEDS.map((seed) => ({ ...seed, phases: phasesFor(seed.id) }));

/** Capability groupings used to organise the grid. */
export const CAPABILITY_GROUPS = [
  { id: "design", label: "Design", body: "Shape the target before you build it." },
  { id: "deliver", label: "Deliver", body: "Turn design into working change." },
  { id: "sustain", label: "Sustain", body: "Keep it healthy after the programme ends." },
];

/**
 * Capabilities grouped for display.
 *
 * @returns {Array<{ id: string, label: string, body: string, items: Capability[] }>}
 */
export function getCapabilitiesByGroup() {
  return CAPABILITY_GROUPS.map((group) => ({
    ...group,
    items: CAPABILITIES.filter((capability) => capability.group === group.id),
  }));
}

/**
 * Find a capability by id.
 *
 * @param {string} id
 * @returns {Capability|undefined}
 */
export function getCapabilityById(id) {
  return CAPABILITIES.find((capability) => capability.id === id);
}
